import React, { useEffect, useState } from 'react';
import { ChevronUp, ChevronDown } from '@insurancemenu/imenu-components-lib/dist/icons';
import { RadioButton } from '@insurancemenu/imenu-components-lib';
import { AcceptedFiles } from '@insurancemenu/imenu-components-lib/dist/types/FileUpload';
import { observer } from 'mobx-react-lite';
import {
  FileUploadStyled,
  IMInputTextStyled,
  RadioButtonGroupStyled,
  SubtitleStyled,
  TemplateSectionStyled,
  ToggleButtonStyled,
} from './styles';
import {
  SHOW_LINK_OPTION,
  SHOW_UPLOAD_OPTION,
  SHOW_UPLOAD_OPTION_TEST_ID,
  SHOW_LINK_OPTION_TEST_ID,
  UPLOAD_TEMPLATE_TEST_ID,
  ADD_TEMPLATE_SECTION_TEST_ID,
  TEMPLATE_LINK_TEST_ID,
  ADD_TEMPLATE_TOGGLE_BUTTON_TEST_ID,
  ADD_TEMPLATE_RADIO_BUTTON_GROUP_TEST_ID,
} from '../constants';

export interface AddTemplateSectionProps {
  /** Unique form ID used for radio buttons */
  formId: string;
  /** Resets dropzone file when the modal closes */
  shouldResetUploadedFile: boolean;
  /** Callback to pass selected file to parent */
  setFile: (files: AcceptedFiles | null) => void;
  /** Sets the initial open state of the section */
  isSectionOpenByDefault?: boolean;
  /** Closes the section when the parent modal is closed */
  shouldCollapseOnClose?: boolean;
}

type TemplateSource = 'LINK' | 'UPLOAD';

const AddTemplateSection: React.FC<AddTemplateSectionProps> = observer(
  ({ formId, shouldResetUploadedFile, isSectionOpenByDefault = false, shouldCollapseOnClose = false, setFile }) => {
    const [isSectionOpen, setIsSectionOpen] = useState(isSectionOpenByDefault || false);
    const [templateSource, setTemplateSource] = useState<TemplateSource>('LINK');

    // Close the section and reset state when modal closes (if needed)
    useEffect(() => {
      if (shouldCollapseOnClose && !isSectionOpenByDefault) {
        setIsSectionOpen(false);
        setTemplateSource('LINK');
      }
    }, [shouldCollapseOnClose]);

    const onToggle = (): void => {
      setIsSectionOpen((prev) => !prev);
    };

    function handleSourceChange(e: React.ChangeEvent<HTMLInputElement>): void {
      setTemplateSource(e.target.value as TemplateSource);
      setFile(null);
    }

    return (
      <TemplateSectionStyled data-test-id={ADD_TEMPLATE_SECTION_TEST_ID}>
        <ToggleButtonStyled
          type="text"
          rightIconProp={
            isSectionOpen ? <ChevronUp data-test-id="chevron-up" /> : <ChevronDown data-test-id="chevron-down" />
          }
          iconSize="3rem"
          onClick={onToggle}
          data-test-id={ADD_TEMPLATE_TOGGLE_BUTTON_TEST_ID}
        >
          Add Template (Optional)
        </ToggleButtonStyled>
        <SubtitleStyled>Choose how you’d like to provide a template for this document.</SubtitleStyled>
        <RadioButtonGroupStyled $isSectionOpen={isSectionOpen} data-test-id={ADD_TEMPLATE_RADIO_BUTTON_GROUP_TEST_ID}>
          <RadioButton
            label="Paste a link to a shared document"
            value="LINK"
            id={`${formId}_${SHOW_LINK_OPTION}`}
            data-test-id={`${formId}_${SHOW_LINK_OPTION_TEST_ID}`}
            checked={templateSource === 'LINK'}
            onChange={handleSourceChange}
          />
          {templateSource === 'LINK' && (
            <div>
              <IMInputTextStyled
                label="Template Link"
                name="templateLink"
                width="auto"
                data-test-id={TEMPLATE_LINK_TEST_ID}
              />
            </div>
          )}

          <RadioButton
            label="Upload a file from your device"
            value="UPLOAD"
            id={`${formId}_${SHOW_UPLOAD_OPTION}`}
            data-test-id={`${formId}_${SHOW_UPLOAD_OPTION_TEST_ID}`}
            checked={templateSource === 'UPLOAD'}
            onChange={handleSourceChange}
          />
          {templateSource === 'UPLOAD' && (
            <FileUploadStyled
              onChange={setFile}
              fileMimeTypes={[]}
              allowMultipleFiles={false}
              shouldBeReset={shouldResetUploadedFile}
              data-test-id={UPLOAD_TEMPLATE_TEST_ID}
            />
          )}
        </RadioButtonGroupStyled>
      </TemplateSectionStyled>
    );
  },
);

export default AddTemplateSection;
